import * as React from 'react';
import { useAtomValue } from 'jotai';
import { useNavigate, useLocation } from 'react-router';
import { match } from 'ts-pattern';
import { useRefreshToken } from '@preview/hooks/useRefreshToken';
import { Unauthenticated } from '@preview/routes/Unauthenticated';
import { authStateAtom, isAuthenticatedAtom } from '@preview/store/auth';

const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const authState = useAtomValue(authStateAtom);
  const { isRefreshing, refreshToken } = useRefreshToken();
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    if (!isAuthenticated && !isRefreshing && location.pathname !== '/') {
      sessionStorage.setItem('redirectPath', location.pathname + location.search);
    }
  }, [isAuthenticated, isRefreshing, location.pathname, location.search]);

  React.useEffect(() => {
    if (isAuthenticated) {
      const redirectPath = sessionStorage.getItem('redirectPath');
      if (redirectPath && redirectPath !== '/') {
        sessionStorage.removeItem('redirectPath');
        navigate(redirectPath, { replace: true });
      }
    }
  }, [isAuthenticated, navigate]);

  React.useEffect(() => {
    if (authState.isExpired && !authState.isRefreshing && authState.token) {
      refreshToken();
    }
  }, [authState.isExpired, authState.isRefreshing, authState.token, refreshToken]);

  return match({ isAuthenticated, isRefreshing })
    .with({ isRefreshing: true }, () => {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            fontSize: '18px',
          }}
        >
          Refreshing session...
        </div>
      );
    })
    .with({ isAuthenticated: false }, () => {
      return <Unauthenticated />;
    })
    .otherwise(() => {
      return <>{children}</>;
    });
};

export default AuthGuard;
